import logo from '@/images/did_logo_dark.svg';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingAnimation from '../../images/LoadingAnimation.json';

import { PlanGroup } from '@d-id/types';

import SimpleNotificationModal from '@/components/Modals/SimpleNotificationModal';
import { toast } from '@/components/toast';
import { useAnalytics } from '@/services/analytics';
import { Billing, goToStripeCheckoutSubscription } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { usePrice } from '@/state/products/Products';
import { PaymentProvider } from '@/state/user-settings/types';
import { AppRoutes } from '@/utils/types';

export default function () {
    const mixpanel = useAnalytics();
    const navigate = useNavigate();
    const { isAnonymous, loginWithRedirect, user } = useAuth();
    const { mutateAsync: handleGoToStripeCheckout } = useMutation('go-to-stripe-checkout', goToStripeCheckoutSubscription);
    const [openMobileModal, setOpenMobileModal] = useState(false);
    const [searchParams] = useSearchParams();
    const priceId = searchParams.get('price') ?? '';
    const utmSource = searchParams.get('utm_source') ?? '';
    const mixpanelBaseProps = {
        requestedPrice: priceId,
        utmSource: utmSource,
        loggedIn: !isAnonymous,
    };
    const { data: price, isFetching: fetchingPrice } = usePrice(priceId);
    const handleCheckout = async ({ priceId, planGroup, billing }: { priceId: string; planGroup: PlanGroup; billing: Billing }) => {
        const currentPlanGroup = user?.planGroup;
        if (!currentPlanGroup || currentPlanGroup === PlanGroup.TRIAL || currentPlanGroup === PlanGroup.FREE) {
            // new subsciption
            try {
                const url = (await handleGoToStripeCheckout({ planGroup, billing, priceId })).url;
                window.location.href = url;
            } catch (error: any) {
                if (error.message === 'user already has a subscription' || error.description === 'user already has a subscription') {
                    mixpanel?.track('checkout', {
                        ...mixpanelBaseProps,
                        requestedPlan: planGroup,
                        requestedBilling: billing,
                        cause: 'already own',
                    });
                    toast.info({ title: 'Plan already exists. Please cancel your current plan' });
                } else if (
                    [
                        'plan is not allowed for self service',
                        'price id does not exist',
                        'could not find price',
                        'validation failed',
                    ].includes(error.response?.data?.description)
                ) {
                    toast.error({ toastId: 'checkout-plan-error', title: 'Plan error. could not find plan' });
                } else {
                    toast.error({ title: 'Failed redirecting to subscription page, please try again later' });
                    logger.error('failed redirecting to stripe checkout page', { error, billing, planGroup });
                }
                navigate(AppRoutes.VideoStudio);
            }
        } else {
            // plan change
            navigate(AppRoutes.VideoStudio, { state: { newPlan: planGroup, newInterval: billing, newPriceId: priceId } });
        }
    };

    useEffect(() => {
        if (user?.paymentProvider === PaymentProvider.AppStore) {
            mixpanel?.track('mobile-checkout-web', {
                ...mixpanelBaseProps,
                redirect: 'root',
                cause: 'mobile user',
            });
            setOpenMobileModal(true);
            return;
        }
        if (isAnonymous) {
            mixpanel?.track('checkout', { ...mixpanelBaseProps, redirect: 'login' });
            loginWithRedirect({
                redirectUri: window.location.href,
            });
        } else if (!priceId) {
            mixpanel?.track('checkout', { ...mixpanelBaseProps, redirect: 'root', cause: 'not found' });
            toast.error({ title: 'Plan not found' });
            navigate(AppRoutes.VideoStudio);
        } else {
            if (fetchingPrice) return;
            if (!price) {
                mixpanel?.track('checkout', { ...mixpanelBaseProps, redirect: 'root', cause: 'not found' });
                toast.error({ title: 'Plan not found' });
                navigate(AppRoutes.VideoStudio);
            } else {
                const requesetdPlan = price!.metadata.plan_group as PlanGroup;
                const requestedBilling = price!.recurring?.interval as Billing;
                if (requesetdPlan === user?.planGroup && requestedBilling === user?.billingInterval && priceId === user?.priceId) {
                    mixpanel?.track('checkout', {
                        ...mixpanelBaseProps,
                        requesetdPlan,
                        requestedBilling,
                        redirect: 'root',
                        cause: 'already own',
                        status: 'error',
                    });
                    toast.error({ title: 'Plan already exists. Please choose another plan' });
                    navigate(AppRoutes.VideoStudio);
                } else {
                    mixpanel?.track('checkout', { ...mixpanelBaseProps, requesetdPlan, requestedBilling, redirect: 'stripe' });
                    handleCheckout({ priceId, planGroup: requesetdPlan, billing: requestedBilling });
                }
            }
        }
    }, [price, isAnonymous, fetchingPrice]);

    const CheckoutWrapper = styled.div`
        display: flex;
        flex-direction: column;
        flex: 1;
        background-color: white;
    `;

    const Logo = styled.img`
        width: 72px;
        height: 72px;
        margin: 72px 0 0 72px;
    `;

    const Loader = styled.div`
        display: flex;
        flex-direction: column;
        align-self: center;
        justify-content: center;
        flex: 1;
        padding-bottom: 200px;
    `;

    const Loading = styled(Player)`
        height: 25rem;
        width: 70%;
    `;

    const Text = styled.div`
        font-style: normal;
        font-weight: 700;
        font-size: 24px;
        line-height: 30px;
        letter-spacing: -0.02em;
        align-self: center;
    `;

    return (
        <>
            <CheckoutWrapper>
                <Logo src={logo} alt="logo" />
                <Loader>
                    <Loading src={LoadingAnimation} autoplay loop />
                    <Text>Fetching your new plan...</Text>
                </Loader>
            </CheckoutWrapper>
            <SimpleNotificationModal
                open={openMobileModal}
                onClose={() => {
                    setOpenMobileModal(false);
                }}
                content="Please use the mobile app to manage your plan."
                primaryActionContent="Got It"
                onPrimaryAction={() => {
                    setOpenMobileModal(false);
                }}
                buttonPlacement="center"
            />
        </>
    );
}
